import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import {
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	MenuItem,
	TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { PdfButton } from "@/controls/buttons/PdfButton";
import type { ReturnedRollsDateBasis } from "../models/ReturnedRolls";

interface Props {
	start: Moment;
	end: Moment;
	dateBasis: ReturnedRollsDateBasis;
	scrapped: boolean;
	broken: boolean;
	returned: boolean;
	awaitingReturn: boolean;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	onDateBasisChange: (value: ReturnedRollsDateBasis) => void;
	onScrappedChange: (value: boolean) => void;
	onBrokenChange: (value: boolean) => void;
	onReturnedChange: (value: boolean) => void;
	onAwaitingReturnChange: (value: boolean) => void;
	onExportExcel: () => void;
	onExportPdf: () => void;
	canExportPdf: boolean;
	exportingPdf: boolean;
	onReset: () => void;
	canReset: boolean;
}

export const ReturnedRollsToolbar = ({
	start,
	end,
	dateBasis,
	scrapped,
	broken,
	returned,
	awaitingReturn,
	onStartChange,
	onEndChange,
	onDateBasisChange,
	onScrappedChange,
	onBrokenChange,
	onReturnedChange,
	onAwaitingReturnChange,
	onExportExcel,
	onExportPdf,
	canExportPdf,
	exportingPdf,
	onReset,
	canReset,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Close"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			{/* Which date the range applies to: the return, or the retirement (RemovedTimestamp). */}
			<TextField
				select
				size="small"
				label="Date by"
				value={dateBasis}
				onChange={(e) =>
					onDateBasisChange(e.target.value as ReturnedRollsDateBasis)
				}
				sx={{ width: 130 }}
			>
				<MenuItem value="RETURNED">Returned</MenuItem>
				<MenuItem value="RETIRED">Retired</MenuItem>
			</TextField>

			<FormControlLabel
				sx={{ mr: 0 }}
				control={
					<Checkbox
						size="small"
						checked={scrapped}
						onChange={(e) => onScrappedChange(e.target.checked)}
					/>
				}
				label="Scrapped"
			/>
			<FormControlLabel
				sx={{ mr: 0 }}
				control={
					<Checkbox
						size="small"
						checked={broken}
						onChange={(e) => onBrokenChange(e.target.checked)}
					/>
				}
				label="Broken"
			/>
			<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
			<FormControlLabel
				sx={{ mr: 0 }}
				control={
					<Checkbox
						size="small"
						checked={returned}
						onChange={(e) => onReturnedChange(e.target.checked)}
					/>
				}
				label="Returned"
			/>
			<FormControlLabel
				sx={{ mr: 0 }}
				control={
					<Checkbox
						size="small"
						checked={awaitingReturn}
						onChange={(e) => onAwaitingReturnChange(e.target.checked)}
					/>
				}
				label="Awaiting return"
			/>

			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ display: "flex", gap: 1 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
				<PdfButton
					handleExportToPdf={onExportPdf}
					disabled={!canExportPdf}
					loading={exportingPdf}
				/>
			</Box>
		</Box>
	);
};
